import type {
  AuthorableLabelType,
  CreativeLabel,
  LabelSchemaDiff,
  LabelScope,
  LabelSuggestion,
  LabelUiType,
  ParsedSuggestions,
} from '../models/labelEditor.types'
import type {
  CreativeLabelDefinition,
  CreativeLabelOption,
  CreativeLabelSchema,
  CreativeLabelSchemaWrite,
  LabelSchemaResponse,
} from '../models/labelSchema.types'

export const AUTHORABLE_LABEL_TYPES = [
  'yesno',
  'multiple',
] as const satisfies readonly AuthorableLabelType[]

export const LABEL_NAME_MAX = 50
export const LABEL_DESCRIPTION_MAX = 1000
export const LABEL_OPTION_VALUE_MAX = 40
export const LABEL_OPTION_DESCRIPTION_MAX = 200
export const COMPUTED_KEY_PREFIX = 'computed_'

export function capitalizeOptionLabel(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function labelTypeKey(type: LabelUiType): string {
  return `creativeAi.settings.type.${type}`
}

export function isAuthorableLabelType(type: LabelUiType): type is AuthorableLabelType {
  return (AUTHORABLE_LABEL_TYPES as readonly string[]).includes(type)
}

export function toSnakeCase(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/gu, '')
    .trim()
    .replace(/[^a-zA-Z0-9]+/gu, '_')
    .replace(/^_+|_+$/gu, '')
    .toLowerCase()
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function optionsFrom(definition: CreativeLabelDefinition): CreativeLabelOption[] {
  return clone(definition.values ?? definition.enum ?? [])
}

export function isComputedField(label: Pick<CreativeLabel, 'key' | 'readonly'>): boolean {
  return label.readonly || label.key.startsWith(COMPUTED_KEY_PREFIX)
}

export function schemaToDraft(schema: LabelSchemaResponse): CreativeLabel[] {
  let id = 0
  const rows = (scope: LabelScope, source: CreativeLabelSchema): CreativeLabel[] =>
    Object.entries(source).map(([key, definition]) => ({
      id: ++id,
      key,
      scope,
      type: definition.type,
      name: definition.name,
      description: definition.description ?? '',
      options: optionsFrom(definition),
      raw: clone(definition),
      readonly: definition.readonly === true,
    }))
  return [...rows('image', schema.image_schema), ...rows('video', schema.video_schema)]
}

function definitionFrom(label: CreativeLabel): CreativeLabelDefinition {
  const definition: CreativeLabelDefinition = {
    ...clone(label.raw),
    name: label.name,
    type: label.type,
    ...(label.description ? { description: label.description } : {}),
  }
  if (!label.description) delete definition.description
  if (label.type === 'multiple') {
    if (label.raw.enum !== undefined && label.raw.values === undefined) {
      definition.enum = clone(label.options)
    } else {
      definition.values = clone(label.options)
    }
  } else {
    delete definition.values
    delete definition.enum
  }
  return definition
}

/**
 * Which scopes actually changed between `draft` and `original` — reuses `diffSchema`'s own
 * added/removed/changed keys rather than re-deriving them, so there is one place that decides
 * "did anything change" (§ `diffSchema`) and one place that turns that into "which scope(s)".
 * A key that moved scope shows as an add in the new scope and a remove in the old one, so both
 * sides get checked against both `draft` and `original` to catch it either way.
 */
export function touchedScopes(
  draft: readonly CreativeLabel[],
  original: readonly CreativeLabel[],
): Set<LabelScope> {
  const diff = diffSchema(draft, original)
  const changedKeys = new Set([
    ...diff.added,
    ...diff.removed,
    ...diff.values_changed,
    ...diff.type_changed,
  ])
  const scopes = new Set<LabelScope>()
  for (const label of draft) if (changedKeys.has(label.key)) scopes.add(label.scope)
  for (const label of original) if (changedKeys.has(label.key)) scopes.add(label.scope)
  return scopes
}

/**
 * A full replace, but only for scopes that actually changed — the backend leaves a scope whose
 * top-level key is absent entirely untouched, while an empty `{}` for a scope that IS present
 * deletes everything in it (`label_schema_service.py`'s `_park_pending_change`). Sending both
 * scopes unconditionally, as a no-op full round-trip of an untouched one, risks wiping it out
 * from a stale/incomplete client read. Computed fields (`isComputedField`) are dropped from every
 * scope that IS sent — the backend manages them itself and rejects a write that echoes one back.
 */
export function draftToWriteSchemas(
  draft: readonly CreativeLabel[],
  original: readonly CreativeLabel[],
): Partial<Record<'image_schema' | 'video_schema', CreativeLabelSchemaWrite>> {
  const result: Partial<Record<'image_schema' | 'video_schema', CreativeLabelSchemaWrite>> = {}
  for (const scope of touchedScopes(draft, original)) {
    const schema: CreativeLabelSchemaWrite = {}
    for (const label of draft) {
      if (label.scope !== scope || isComputedField(label)) continue
      schema[label.key] = definitionFrom(label)
    }
    result[scope === 'image' ? 'image_schema' : 'video_schema'] = schema
  }
  return result
}

function comparable(label: CreativeLabel): string {
  return JSON.stringify(definitionFrom(label))
}

export function diffSchema(
  draft: readonly CreativeLabel[],
  original: readonly CreativeLabel[],
): LabelSchemaDiff {
  const current = new Map(draft.map((label) => [`${label.scope}:${label.key}`, label]))
  const baseline = new Map(original.map((label) => [`${label.scope}:${label.key}`, label]))
  const added: string[] = []
  const removed: string[] = []
  const values_changed: string[] = []
  const type_changed: string[] = []
  const dirtyIds = new Set<number>()

  for (const [key, label] of current) {
    const prior = baseline.get(key)
    if (!prior) {
      added.push(label.key)
      dirtyIds.add(label.id)
    } else if (label.type !== prior.type) {
      type_changed.push(label.key)
      dirtyIds.add(label.id)
    } else if (comparable(label) !== comparable(prior)) {
      values_changed.push(label.key)
      dirtyIds.add(label.id)
    }
  }
  for (const [key, label] of baseline) {
    if (!current.has(key)) removed.push(label.key)
  }
  return {
    added,
    removed,
    values_changed,
    type_changed,
    dirtyIds,
    count: added.length + removed.length + values_changed.length + type_changed.length,
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function parseOption(value: unknown): CreativeLabelOption | null {
  if (typeof value === 'string') {
    return { value, display_name: capitalizeOptionLabel(value), description: null }
  }
  const row = record(value)
  if (!row || typeof row.value !== 'string') return null
  return {
    value: row.value,
    display_name:
      typeof row.display_name === 'string' ? row.display_name : capitalizeOptionLabel(row.value),
    description: typeof row.description === 'string' ? row.description : null,
  }
}

function parseSuggestion(value: unknown): LabelSuggestion | null {
  const row = record(value)
  if (!row || (row.scope !== 'image' && row.scope !== 'video')) return null
  if (row.type !== 'yesno' && row.type !== 'multiple') return null
  if (typeof row.key !== 'string' || typeof row.name !== 'string') return null
  const values = Array.isArray(row.values)
    ? row.values.map(parseOption).filter((v) => v !== null)
    : []
  return {
    key: row.key,
    scope: row.scope,
    type: row.type,
    name: row.name,
    description: typeof row.description === 'string' ? row.description : '',
    values,
  }
}

export function labelErrorsFromPayload(payload: unknown): Record<string, string> {
  const errorObject = record(payload)?.error
  const details = record(errorObject)?.details
  const list = record(details)?.errors
  if (!Array.isArray(list)) return {}
  const keyed: Record<string, string> = {}
  for (const entry of list) {
    const row = record(entry)
    if (typeof row?.key === 'string' && typeof row.message === 'string')
      keyed[row.key] = row.message
  }
  return keyed
}

export function parseSuggestions(value: unknown): ParsedSuggestions {
  const root = record(value)
  const suggestions = Array.isArray(root?.suggestions)
    ? root.suggestions.map(parseSuggestion).filter((item) => item !== null)
    : []
  return { suggestions, meta: record(root?.meta) ?? {} }
}

export function suggestionToLabel(suggestion: LabelSuggestion, id: number): CreativeLabel {
  const raw: CreativeLabelDefinition = {
    name: suggestion.name,
    type: suggestion.type,
    ...(suggestion.description ? { description: suggestion.description } : {}),
    ...(suggestion.type === 'multiple' ? { values: clone(suggestion.values) } : {}),
  }
  return {
    id,
    key: suggestion.key,
    scope: suggestion.scope,
    type: suggestion.type,
    name: suggestion.name,
    description: suggestion.description,
    options: clone(suggestion.values),
    raw,
    readonly: false,
  }
}
